import React, { useEffect, useState, useRef } from 'react';
import { MessageCircleIcon, XIcon, SendIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
type Sender = 'bot' | 'user';
type Step = 'welcome' | 'service_info' | 'lead_name' | 'lead_phone' | 'lead_email' | 'lead_state' | 'lead_time' | 'confirmation';
interface ButtonOption {
  label: string;
  value: string;
}
interface Message {
  id: string;
  sender: Sender;
  text: string;
  buttons?: ButtonOption[];
}
export function ChatbotWidget() {
  const {
    lang: globalLang,
    setLang: setGlobalLang
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  const [inputValue, setInputValue] = useState('');
  const [leadData, setLeadData] = useState<Record<string, string>>({});
  const [selectedService, setSelectedService] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [hasFollowedUp, setHasFollowedUp] = useState(false);
  const [initialized, setInitialized] = useState(false);
  // Use global language directly
  const tChat = (es: string, en: string) => globalLang === 'es' ? es : en;
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  // Initialize chat with welcome message on first open
  useEffect(() => {
    if (isOpen && !initialized) {
      showWelcome();
      setInitialized(true);
    }
  }, [isOpen]);
  // Reset chat when global language changes (after first init)
  useEffect(() => {
    if (initialized) {
      setMessages([]);
      setCurrentStep('welcome');
      setLeadData({});
      setSelectedService('');
      setHasFollowedUp(false);
      // Use setTimeout so the state clears before adding the new welcome
      setTimeout(() => {
        const welcomeText = globalLang === 'es' ? 'Hola, gracias por comunicarte con Jesús Silva – Agente de Seguros de Protección Integral. Estoy aquí para ayudarte con información sobre seguros de salud, vida, accidentes y Medicare. ¿Qué tipo de cobertura te interesa hoy?' : "Hello, thank you for contacting Jesus Silva – Agente de Seguros de Protección Integral. I'm here to help you with information about health, life, accident, and Medicare options. What type of coverage are you interested in today?";
        const buttons = globalLang === 'es' ? [{
          label: 'Seguro de Salud',
          value: 'health'
        }, {
          label: 'Seguro de Vida',
          value: 'life'
        }, {
          label: 'Seguro de Accidentes',
          value: 'accident'
        }, {
          label: 'Medicare',
          value: 'medicare'
        }, {
          label: 'Solicitar Información',
          value: 'request_info'
        }] : [{
          label: 'Health Insurance',
          value: 'health'
        }, {
          label: 'Life Insurance',
          value: 'life'
        }, {
          label: 'Accident Insurance',
          value: 'accident'
        }, {
          label: 'Medicare',
          value: 'medicare'
        }, {
          label: 'Request Information',
          value: 'request_info'
        }];
        setMessages([{
          id: Date.now().toString(),
          sender: 'bot',
          text: welcomeText,
          buttons
        }]);
      }, 50);
    }
  }, [globalLang]);
  // Inactivity timer for lead capture steps
  useEffect(() => {
    if (isOpen && !hasFollowedUp && currentStep.startsWith('lead_') && currentStep !== 'lead_time') {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        addBotMessage(tChat('Solo estoy dando seguimiento a tu solicitud. Si deseas, compárteme tu información para ayudarte mejor.', 'Just following up on your request. If you would like, share your information so I can assist you better.'));
        setHasFollowedUp(true);
      }, 30000);
    }
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [isOpen, currentStep, hasFollowedUp, globalLang]);
  const addBotMessage = (text: string, buttons?: ButtonOption[]) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      sender: 'bot',
      text,
      buttons
    }]);
  };
  const addUserMessage = (text: string) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      sender: 'user',
      text
    }]);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  };
  const showWelcome = () => {
    const welcomeText = globalLang === 'es' ? 'Hola, gracias por comunicarte con Jesús Silva – Agente de Seguros de Protección Integral. Estoy aquí para ayudarte con información sobre seguros de salud, vida, accidentes y Medicare. ¿Qué tipo de cobertura te interesa hoy?' : "Hello, thank you for contacting Jesus Silva – Agente de Seguros de Protección Integral. I'm here to help you with information about health, life, accident, and Medicare options. What type of coverage are you interested in today?";
    const buttons = globalLang === 'es' ? [{
      label: 'Seguro de Salud',
      value: 'health'
    }, {
      label: 'Seguro de Vida',
      value: 'life'
    }, {
      label: 'Seguro de Accidentes',
      value: 'accident'
    }, {
      label: 'Medicare',
      value: 'medicare'
    }, {
      label: 'Solicitar Información',
      value: 'request_info'
    }] : [{
      label: 'Health Insurance',
      value: 'health'
    }, {
      label: 'Life Insurance',
      value: 'life'
    }, {
      label: 'Accident Insurance',
      value: 'accident'
    }, {
      label: 'Medicare',
      value: 'medicare'
    }, {
      label: 'Request Information',
      value: 'request_info'
    }];
    setCurrentStep('welcome');
    addBotMessage(welcomeText, buttons);
  };
  const handleServiceSelect = (service: string, label: string) => {
    if (service === 'request_info') {
      startLeadCapture();
      return;
    }
    setSelectedService(label);
    let infoText = '';
    if (service === 'health') {
      infoText = tChat('Te ayudo a explorar opciones de seguro de salud según tus necesidades y presupuesto.', 'I can help you explore health insurance options based on your needs and budget.');
    } else if (service === 'life') {
      infoText = tChat('Te ayudo a conocer opciones de seguro de vida para proteger a tu familia.', 'I can help you review life insurance options to help protect your family.');
    } else if (service === 'accident') {
      infoText = tChat('Te ayudo a revisar opciones de cobertura para gastos inesperados por accidentes.', 'I can help you review accident coverage options for unexpected expenses.');
    } else if (service === 'medicare') {
      infoText = tChat('Te ayudo con orientación general sobre opciones de Medicare.', 'I can help with general Medicare guidance so you can better understand your options.');
    }
    setCurrentStep('service_info');
    addBotMessage(infoText, [{
      label: tChat('Solicitar información', 'Request Information'),
      value: 'request_info'
    }, {
      label: tChat('Quiero una llamada', 'Call Me Back'),
      value: 'call_back'
    }, {
      label: tChat('Volver al inicio', 'Start Over'),
      value: 'start_over'
    }]);
  };
  const startLeadCapture = () => {
    setCurrentStep('lead_name');
    addBotMessage(tChat('¿Cuál es tu nombre completo?', 'What is your full name?'));
  };
  const handleButtonClick = (value: string, label: string) => {
    addUserMessage(label);
    // Remove buttons from previous message to prevent clicking again
    setMessages(prev => {
      const newMessages = [...prev];
      const lastBotMsgIndex = newMessages.map(m => m.sender).lastIndexOf('bot');
      if (lastBotMsgIndex !== -1) {
        newMessages[lastBotMsgIndex] = {
          ...newMessages[lastBotMsgIndex],
          buttons: undefined
        };
      }
      return newMessages;
    });
    setTimeout(() => {
      if (currentStep === 'welcome') {
        handleServiceSelect(value, label);
      } else if (currentStep === 'service_info') {
        if (value === 'start_over') {
          resetChat();
        } else {
          startLeadCapture();
        }
      } else if (currentStep === 'lead_time') {
        handleLeadInput(value, label);
      } else if (currentStep === 'confirmation' && value === 'start_over') {
        resetChat();
      }
    }, 500);
  };
  const resetChat = () => {
    setLeadData({});
    setSelectedService('');
    setHasFollowedUp(false);
    showWelcome();
  };
  const handleLeadInput = (value: string, displayLabel?: string) => {
    if (!displayLabel) addUserMessage(value);
    setTimeout(() => {
      if (currentStep === 'lead_name') {
        setLeadData(prev => ({
          ...prev,
          name: value
        }));
        setCurrentStep('lead_phone');
        addBotMessage(tChat('¿Cuál es tu número de teléfono?', 'What is your phone number?'));
      } else if (currentStep === 'lead_phone') {
        setLeadData(prev => ({
          ...prev,
          phone: value
        }));
        setCurrentStep('lead_email');
        addBotMessage(tChat('¿Cuál es tu correo electrónico?', 'What is your email address?'));
      } else if (currentStep === 'lead_email') {
        setLeadData(prev => ({
          ...prev,
          email: value
        }));
        setCurrentStep('lead_state');
        addBotMessage(tChat('¿En qué estado te encuentras?', 'What state are you located in?'));
      } else if (currentStep === 'lead_state') {
        setLeadData(prev => ({
          ...prev,
          state: value
        }));
        setCurrentStep('lead_time');
        addBotMessage(tChat('¿Cuál es el mejor horario para contactarte?', 'What is the best time to contact you?'), [{
          label: tChat('Mañana', 'Morning'),
          value: 'morning'
        }, {
          label: tChat('Tarde', 'Afternoon'),
          value: 'afternoon'
        }, {
          label: tChat('Noche', 'Evening'),
          value: 'evening'
        }]);
      } else if (currentStep === 'lead_time') {
        setLeadData(prev => ({
          ...prev,
          time: displayLabel || value
        }));
        setCurrentStep('confirmation');
        addBotMessage(tChat('Gracias. Hemos recibido tu información. Un asesor se comunicará contigo pronto para ayudarte según tus necesidades.', 'Thank you. We have received your information. An advisor will contact you soon to assist you based on your needs.'), [{
          label: tChat('Volver al inicio', 'Start Over'),
          value: 'start_over'
        }]);
        // Here you would typically send the leadData to your backend
        console.log('Lead captured:', {
          ...leadData,
          time: displayLabel || value,
          service: selectedService
        });
      }
    }, 500);
  };
  const handleFreeTextQuestion = (text: string) => {
    const lower = text.toLowerCase();
    // Keyword matching for common questions
    const healthKeywords = ['salud', 'health', 'médico', 'doctor', 'hospital', 'medical'];
    const lifeKeywords = ['vida', 'life', 'fallecimiento', 'death', 'familia', 'family'];
    const accidentKeywords = ['accidente', 'accident', 'lesión', 'injury', 'emergencia', 'emergency'];
    const medicareKeywords = ['medicare', 'medicaid', '65', 'jubilación', 'retirement', 'senior'];
    const contactKeywords = ['contacto', 'contact', 'llamar', 'call', 'teléfono', 'phone', 'hablar', 'speak', 'cita', 'appointment'];
    const priceKeywords = ['precio', 'price', 'costo', 'cost', 'cuánto', 'how much', 'cotización', 'quote'];
    const greetKeywords = ['hola', 'hello', 'hi', 'buenos', 'good', 'hey', 'buenas'];
    if (greetKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('¡Hola! ¿En qué puedo ayudarte hoy? Puedes elegir una opción o escribirme tu pregunta.', 'Hello! How can I help you today? You can choose an option or type your question.'));
    } else if (healthKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('Te ayudo a explorar opciones de seguro de salud según tus necesidades y presupuesto. ¿Te gustaría que un asesor te contacte?', 'I can help you explore health insurance options based on your needs and budget. Would you like an advisor to contact you?'), [{
        label: tChat('Sí, quiero información', 'Yes, I want info'),
        value: 'request_info'
      }, {
        label: tChat('Ver otros servicios', 'See other services'),
        value: 'start_over'
      }]);
    } else if (lifeKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('Te ayudo a conocer opciones de seguro de vida para proteger a tu familia. ¿Quieres que un asesor te contacte?', 'I can help you review life insurance options to protect your family. Would you like an advisor to contact you?'), [{
        label: tChat('Sí, quiero información', 'Yes, I want info'),
        value: 'request_info'
      }, {
        label: tChat('Ver otros servicios', 'See other services'),
        value: 'start_over'
      }]);
    } else if (accidentKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('Te ayudo a revisar opciones de cobertura para gastos inesperados por accidentes. ¿Te gustaría más información?', 'I can help you review accident coverage options for unexpected expenses. Would you like more information?'), [{
        label: tChat('Sí, quiero información', 'Yes, I want info'),
        value: 'request_info'
      }, {
        label: tChat('Ver otros servicios', 'See other services'),
        value: 'start_over'
      }]);
    } else if (medicareKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('Te ayudo con orientación general sobre opciones de Medicare para que tomes una mejor decisión. ¿Quieres que te contactemos?', 'I can help with general Medicare guidance so you can better understand your options. Would you like us to contact you?'), [{
        label: tChat('Sí, quiero información', 'Yes, I want info'),
        value: 'request_info'
      }, {
        label: tChat('Ver otros servicios', 'See other services'),
        value: 'start_over'
      }]);
    } else if (contactKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('Con gusto te ponemos en contacto con un asesor. Déjame tus datos para que te contactemos.', 'We would be happy to connect you with an advisor. Let me collect your information so we can reach out.'));
      setTimeout(() => startLeadCapture(), 800);
    } else if (priceKeywords.some(k => lower.includes(k))) {
      addBotMessage(tChat('Los costos varían según tu situación y necesidades. Un asesor puede darte una cotización personalizada sin compromiso. ¿Te gustaría que te contactemos?', 'Costs vary depending on your situation and needs. An advisor can give you a personalized quote with no obligation. Would you like us to contact you?'), [{
        label: tChat('Sí, quiero cotización', 'Yes, I want a quote'),
        value: 'request_info'
      }, {
        label: tChat('Ver servicios', 'See services'),
        value: 'start_over'
      }]);
    } else {
      addBotMessage(tChat('Gracias por tu mensaje. Para darte la mejor atención, te recomiendo elegir una de las opciones o dejar tus datos para que un asesor te contacte personalmente.', 'Thank you for your message. For the best assistance, I recommend choosing one of the options or leaving your information so an advisor can contact you personally.'), [{
        label: tChat('Solicitar información', 'Request Information'),
        value: 'request_info'
      }, {
        label: tChat('Ver servicios', 'See services'),
        value: 'start_over'
      }]);
    }
  };
  const handleTextInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const val = inputValue.trim();
    setInputValue('');
    // If we're in a lead capture step, handle as lead input
    if (currentStep.startsWith('lead_') && currentStep !== 'lead_time') {
      handleLeadInput(val);
    } else {
      // Otherwise handle as a free-text question
      addUserMessage(val);
      setTimeout(() => handleFreeTextQuestion(val), 500);
    }
  };
  return <>
      {/* Floating Button */}
      <button onClick={() => setIsOpen(true)} className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#1a365d] text-white rounded-full shadow-xl flex items-center justify-center hover:bg-blue-900 transition-transform transform hover:scale-105 ${isOpen ? 'hidden' : 'flex'}`} aria-label="Open chat" data-id="element-23">
        <MessageCircleIcon className="w-7 h-7" data-id="element-24" />
      </button>

      {/* Chat Window */}
      <AnimatePresence data-id="element-25">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="fixed bottom-6 right-4 sm:right-6 z-50 w-[calc(100%-2rem)] sm:w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-200" style={{
        maxHeight: 'calc(100vh - 100px)',
        height: '600px'
      }} data-id="element-26">
            {/* Header */}
            <div className="bg-[#1a365d] text-white px-4 py-3 flex justify-between items-center shrink-0" data-id="element-27">
              <div className="flex items-center gap-2" data-id="element-28">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center" data-id="element-29">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-30" />
                </div>
                <div data-id="element-31">
                  <h3 className="font-semibold text-sm" data-id="element-32">Jesús Silva</h3>
                  <p className="text-xs text-blue-200" data-id="element-33">
                    {tChat('Asesor de Seguros', 'Insurance Advisor')}
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-md transition-colors" data-id="element-34">
                <ChevronDownIcon className="w-5 h-5" data-id="element-35" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" data-id="element-36">
              {messages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-37">
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.sender === 'user' ? 'bg-[#1a365d] text-white rounded-tr-sm' : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm shadow-sm'}`} data-id="element-38">
                    {msg.text}
                  </div>

                  {msg.buttons && msg.buttons.length > 0 && <div className="flex flex-wrap gap-2 mt-3 w-full" data-id="element-39">
                      {msg.buttons.map((btn, idx) => <button key={idx} onClick={() => handleButtonClick(btn.value, btn.label)} className="bg-white border border-[#1a365d] text-[#1a365d] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#1a365d] hover:text-white transition-colors shadow-sm" data-id="element-40">
                          {btn.label}
                        </button>)}
                    </div>}
                </div>)}
              <div ref={messagesEndRef} data-id="element-41" />
            </div>

            {/* Input Area — always visible */}
            <div className="p-3 bg-white border-t border-gray-200 shrink-0" data-id="element-42">
              <form onSubmit={handleTextInputSubmit} className="flex gap-2" data-id="element-43">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={tChat('Escribe tu pregunta o respuesta...', 'Type your question or answer...')} className="flex-1 px-4 py-2 bg-gray-100 border-transparent focus:bg-white focus:border-[#d97706] focus:ring-2 focus:ring-[#d97706]/20 rounded-full text-sm transition-all outline-none" data-id="element-44" />
                <button type="submit" disabled={!inputValue.trim()} className="w-10 h-10 bg-[#d97706] text-white rounded-full flex items-center justify-center hover:bg-[#b45309] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0" data-id="element-45">
                  <SendIcon className="w-4 h-4 ml-0.5" data-id="element-46" />
                </button>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
// Need to import ShieldCheckIcon since I used it in the header
function ShieldCheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-47">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" data-id="element-48" />
      <path d="m9 12 2 2 4-4" data-id="element-49" />
    </svg>;
}